import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, {
  dateFormatter,
  buttonsStyle,
} from "../../../components/tables/table";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  getImagenes,
  deleteImagen,
  getImageUsage,
  getImageTags,
  updateImagen,
} from "../../../../api/imagen";
import {
  Button,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewListIcon from "@material-ui/icons/ViewList";
import SearchIcon from "@material-ui/icons/Search";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import InfoIcon from "@material-ui/icons/Info";
import DescriptionIcon from "@material-ui/icons/Description";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Autorenew as FilterOffIcon } from "@material-ui/icons";
import Select from "react-select";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import ImageCarouselDialog from "../../../components/dialogs/ImageCarouselDialog";
import { SERVER_URL } from "../../../../api";

function getData(imagenes) {
  let data = [];
  for (let i = 0; i < imagenes.length; ++i) {
    const elem = {};
    elem.titulo = imagenes[i].titulo || "";
    elem.url = imagenes[i].url;
    elem.generalUrl = (SERVER_URL + "/" + imagenes[i].url).replaceAll(
      /\\/g,
      "/",
    );
    elem.id = imagenes[i]._id;
    elem.createdAt = imagenes[i].createdAt;
    elem.etiquetas = imagenes[i].etiquetas || [];
    elem.favorito = imagenes[i].favorito || false;
    elem.tipo = imagenes[i].tipo || "imagen"; // 'imagen' o 'archivo'
    elem.tipoArchivo = imagenes[i].tipoArchivo || ""; // 'pdf', 'doc', etc.
    data = data.concat(elem);
  }
  return data;
}

const initialFilters = {
  searchText: "",
  tag: null,
  favorito: false,
  tipo: null, // 'imagen', 'archivo', o null para todos
};

export default function ImagenesPage() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [imagenId, setImagenId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const history = useHistory();
  const user = useSelector((store) => store.authentication?.user, shallowEqual);

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Filtros y búsqueda
  const [filterOptions, setFilterOptions] = useState(initialFilters);
  const [searchText, setSearchText] = useState(() => {
    const saved = localStorage.getItem("imagenesPage_searchText");
    return saved || "";
  });

  // Vista (tabla o cuadrícula)
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("imagenesPage_viewMode");
    return saved || "table";
  });

  // Selección múltiple
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  // Tags y favoritos
  const [availableTags, setAvailableTags] = useState([]); // Tags disponibles en el sistema
  const [selectedTags, setSelectedTags] = useState([]); // Tags seleccionadas para filtrar
  const [tagInput, setTagInput] = useState(""); // Input temporal para escribir tags
  const [showFavorites, setShowFavorites] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState(null); // 'imagen', 'archivo', o null

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 50;

  // Información de uso
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const handleTableSearchChange = useCallback((value) => {
    const trimmedValue = value ? value : "";
    setSearchText(trimmedValue);
    if (trimmedValue) {
      localStorage.setItem("imagenesPage_searchText", trimmedValue);
    } else {
      localStorage.removeItem("imagenesPage_searchText");
    }
  }, []);

  const handleSearch = useCallback(() => {
    // La búsqueda ahora se hace en el backend, solo recargamos
    setCurrentPage(1);
    setRefresh(true);
  }, []);

  const handleToggleFavorite = async (imageId, currentFavorite) => {
    try {
      const image = data.find((img) => img.id === imageId);
      if (!image) return;

      await updateImagen(imageId, { ...image, favorito: !currentFavorite });
      setRefresh(true);
      alertSuccess({
        title: "¡Actualizado!",
        customMessage: currentFavorite
          ? "Imagen eliminada de favoritos."
          : "Imagen añadida a favoritos.",
      });
    } catch (error) {
      alertError({
        error: error,
        customMessage: "No se ha podido actualizar la imagen.",
      });
    }
  };

  const handleShowUsage = async (imageId) => {
    setLoadingUsage(true);
    setUsageDialogOpen(true);
    try {
      const res = await getImageUsage(imageId);
      if (res.status === 200) {
        setUsageData(res.data);
      }
    } catch (error) {
      alertError({
        error: error,
        customMessage: "No se ha podido obtener la información de uso.",
      });
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText("");
    setSelectedTags([]);
    setTagInput("");
    setShowFavorites(false);
    setSelectedTipo(null);
    setFilterOptions(initialFilters);
    localStorage.removeItem("imagenesPage_searchText");
    setCurrentPage(1);
    setRefresh(true);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTagToFilter();
    }
  };

  const addTagToFilter = () => {
    const tagValue = tagInput.trim().toLowerCase();
    if (tagValue && !selectedTags.includes(tagValue)) {
      const newTags = [...selectedTags, tagValue];
      setSelectedTags(newTags);
      setTagInput("");
      setCurrentPage(1);
      setRefresh(true);
    } else if (tagValue) {
      // Si ya existe, solo limpiar el input
      setTagInput("");
    }
  };

  const removeTagFromFilter = (tagToRemove) => {
    const newTags = selectedTags.filter((tag) => tag !== tagToRemove);
    setSelectedTags(newTags);
    setCurrentPage(1);
    setRefresh(true);
  };

  // Función para obtener icono según tipo de archivo
  const getFileIcon = (tipo, tipoArchivo) => {
    if (tipo === "imagen") return null; // Las imágenes se muestran directamente

    const tipoLower = (tipoArchivo || "").toLowerCase();
    switch (tipoLower) {
      case "pdf":
        return <PictureAsPdfIcon style={{ fontSize: 48, color: "#d32f2f" }} />;
      case "doc":
      case "docx":
        return <DescriptionIcon style={{ fontSize: 48, color: "#1976d2" }} />;
      case "xls":
      case "xlsx":
        return <DescriptionIcon style={{ fontSize: 48, color: "#388e3c" }} />;
      case "ppt":
      case "pptx":
        return <DescriptionIcon style={{ fontSize: 48, color: "#f57c00" }} />;
      default:
        return (
          <InsertDriveFileIcon style={{ fontSize: 48, color: "#757575" }} />
        );
    }
  };

  const handleToggleSelect = (imageId) => {
    setSelectedImages((prev) => {
      if (prev.includes(imageId)) {
        return prev.filter((id) => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.length === filteredData.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(filteredData.map((item) => item.id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedImages.length === 0) return;
    setOpenConfirmDialog(3); // Dialog para eliminar múltiples
  };

  function buttonFormatter(cell) {
    const elem = filteredData.find((item) => item.id === cell);
    const isSelected = selectedImages.includes(cell);
    const isFavorite = elem?.favorito || false;

    return (
      <>
        {selectMode && (
          <Checkbox
            checked={isSelected}
            onChange={() => handleToggleSelect(cell)}
            style={{ marginRight: 8 }}
          />
        )}
        {!selectMode && (
          <>
            <Tooltip
              title={isFavorite ? "Quitar de favoritos" : "Añadir a favoritos"}
            >
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleToggleFavorite(cell, isFavorite)}
              >
                {isFavorite ? (
                  <StarIcon style={{ color: "#f3691a" }} />
                ) : (
                  <StarBorderIcon />
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Ver dónde se usa">
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleShowUsage(cell)}
              >
                <InfoIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Editar">
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => history.push("/edit-imagen/" + cell)}
              >
                <EditIcon />
              </Button>
            </Tooltip>
            {(user?.role === "admin" || user.role === "gestor") && (
              <Tooltip title="Eliminar">
                <Button
                  style={buttonsStyle}
                  size="small"
                  onClick={() => {
                    setImagenId(cell);
                    setOpenConfirmDialog(2);
                  }}
                >
                  <DeleteIcon />
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </>
    );
  }
  function imageFormatter(imagesURL, row) {
    const isSelected = selectMode && selectedImages.includes(row.id);
    const isImage = row.tipo === "imagen";
    const fileIcon = getFileIcon(row.tipo, row.tipoArchivo);

    return imagesURL ? (
      <div
        style={{
          position: "relative",
          width: "100px",
          height: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selectMode && (
          <Checkbox
            checked={isSelected}
            onChange={() => handleToggleSelect(row.id)}
            style={{
              position: "absolute",
              top: 5,
              left: 5,
              zIndex: 10,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          />
        )}
        {isImage ? (
          <img
            src={SERVER_URL + "/" + imagesURL}
            alt="artwork"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              cursor: selectMode ? "pointer" : "zoom-in",
              opacity: selectMode && !isSelected ? 0.5 : 1,
              border: isSelected ? "3px solid #f3691a" : "none",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (selectMode) {
                handleToggleSelect(row.id);
              } else {
                // Convertir todas las imágenes filtradas a array y encontrar el índice de la actual
                const allImages = filteredData
                  .filter((item) => item.tipo === "imagen")
                  .map((item) => item.url);
                const currentIndex = allImages.findIndex(
                  (url) => url === imagesURL,
                );
                setPreviewImages(allImages);
                setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
                setOpenPreviewDialog(true);
              }
            }}
          />
        ) : (
          <div
            style={{
              width: "100px",
              height: "100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              border: isSelected ? "3px solid #f3691a" : "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: "#f5f5f5",
              cursor: selectMode ? "pointer" : "default",
              opacity: selectMode && !isSelected ? 0.5 : 1,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (selectMode) {
                handleToggleSelect(row.id);
              }
            }}
          >
            {fileIcon}
            {row.tipoArchivo && (
              <span
                style={{ fontSize: "10px", marginTop: "4px", color: "#666" }}
              >
                {row.tipoArchivo.toUpperCase()}
              </span>
            )}
          </div>
        )}
      </div>
    ) : (
      <div />
    );
  }

  const columns = [
    {
      dataField: "url",
      text: "",
      formatter: imageFormatter,
      headerStyle: () => ({
        width: "10%",
        textAlign: "center",
      }),
    },
    {
      dataField: "generalUrl",
      text: "URL",
      sort: true,
      headerStyle: () => ({
        width: "50%",
        textAlign: "left",
      }),
    },
    {
      dataField: "titulo",
      text: "Titulo",
      sort: true,
      headerStyle: () => ({
        width: "30%",
        textAlign: "left",
      }),
    },
    {
      dataField: "id",
      text: "",
      formatter: buttonFormatter,
      headerStyle: () => ({
        width: "14%",
        textAlign: "center",
      }),
    },
  ];

  // Cargar tags disponibles
  useEffect(() => {
    getImageTags()
      .then((res) => {
        if (res.status === 200) {
          setAvailableTags(res.data);
        }
      })
      .catch((error) => {
        console.error("Error al cargar tags:", error);
      });
  }, []);

  // Cargar imágenes con paginación y filtros
  useEffect(() => {
    setLoading(true);
    const params = {
      page: currentPage,
      limit: pageSize,
    };

    if (searchText) {
      params.search = searchText;
    }
    // Enviar todas las tags seleccionadas (el backend soporta múltiples tags con OR)
    if (selectedTags.length > 0) {
      params.tag = selectedTags.join(","); // Enviar como string separado por comas
    }
    if (selectedTipo) {
      params.tipo = selectedTipo.value;
    }
    if (showFavorites) {
      params.favorito = "true";
    }

    getImagenes(params)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res.data.data || res.data; // Compatibilidad con respuesta paginada o no
          const processedData = getData(responseData);
          setData(processedData);
          setFilteredData(processedData);

          // Si hay paginación
          if (res.data.pagination) {
            setTotalPages(res.data.pagination.pages);
            setTotalImages(res.data.pagination.total);
          }

          setRefresh(false);
          setLoading(false);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las imagenes.",
        });
        setLoading(false);
      });
  }, [
    refresh,
    currentPage,
    searchText,
    selectedTags,
    showFavorites,
    selectedTipo,
  ]);

  // Eliminado: la búsqueda ahora se hace directamente en el backend

  const renderFiltersContent = () => {
    return (
      <div style={{ padding: "5px 0 0 0" }}>
        <div className="row align-items-end">
          <div className="col-md-3 col-sm-6 mb-3">
            <TextField
              id="search"
              label="Buscar"
              placeholder="Buscar por título o URL..."
              value={searchText}
              onChange={(e) => handleTableSearchChange(e.target.value)}
              margin="dense"
              variant="outlined"
              fullWidth
              style={{ marginTop: 0, marginBottom: 0 }}
            />
          </div>
          <div className="col-md-2 col-sm-6 mb-3">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: 500,
                fontSize: "14px",
                color: "#333",
              }}
            >
              Tipo
            </label>
            <Select
              value={selectedTipo}
              onChange={(selected) => {
                setSelectedTipo(selected);
                setCurrentPage(1);
                setRefresh(true);
              }}
              options={[
                { value: "imagen", label: "Imágenes" },
                { value: "archivo", label: "Archivos" },
              ]}
              placeholder="Todos"
              isClearable
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: "40px",
                  borderColor: "#ced4da",
                }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="col-md-4 col-sm-12 mb-3">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: 500,
                fontSize: "14px",
                color: "#333",
              }}
            >
              Filtrar por etiquetas
            </label>
            <div
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                padding: "8px",
                minHeight: "40px",
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                alignItems: "center",
                backgroundColor: "#fff",
              }}
            >
              {selectedTags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => removeTagFromFilter(tag)}
                  size="small"
                  style={{
                    backgroundColor: "#f3691a",
                    color: "#fff",
                    fontSize: "12px",
                    height: "24px",
                  }}
                />
              ))}
              <TextField
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder={
                  selectedTags.length === 0
                    ? "Escribe una etiqueta y presiona Enter..."
                    : "Añadir otra etiqueta..."
                }
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: "14px" },
                }}
                style={{
                  flex: 1,
                  minWidth: "150px",
                }}
              />
            </div>
            {selectedTags.length > 0 && (
              <Typography
                variant="caption"
                style={{ color: "#666", marginTop: "4px", display: "block" }}
              >
                Filtrando por: {selectedTags.join(", ")}{" "}
                {selectedTags.length > 1 && "(cualquiera de estas etiquetas)"}
              </Typography>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div
              className="d-flex justify-content-between align-items-center flex-wrap"
              style={{ marginTop: "10px", marginBottom: "10px", gap: "15px" }}
            >
              <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: "15px" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showFavorites}
                      onChange={() => {
                        setShowFavorites(!showFavorites);
                        setCurrentPage(1);
                        setRefresh(true);
                      }}
                      name="showFavorites"
                      style={{ color: "#f3691a" }}
                    />
                  }
                  label={
                    showFavorites ? "Solo favoritos" : "Todas las imágenes"
                  }
                  style={{ margin: 0 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectMode}
                      onChange={() => {
                        setSelectMode(!selectMode);
                        if (selectMode) {
                          setSelectedImages([]);
                        }
                      }}
                      name="selectMode"
                      style={{ color: "#f3691a" }}
                    />
                  }
                  label={
                    selectMode ? "Modo selección activo" : "Modo selección"
                  }
                  style={{ margin: 0 }}
                />
                {selectMode && selectedImages.length > 0 && (
                  <span style={{ color: "#f3691a", fontWeight: "bold" }}>
                    {selectedImages.length} seleccionada(s)
                  </span>
                )}
              </div>
              <div className="d-flex">
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  style={{
                    minWidth: "120px",
                    backgroundColor: "#f3691a",
                    color: "white",
                  }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<FilterOffIcon />}
                  style={{
                    minWidth: "120px",
                    marginLeft: "10px",
                    borderColor: "#f3691a",
                    color: "#f3691a",
                  }}
                >
                  Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGridView = () => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "20px",
          padding: "20px",
        }}
      >
        {filteredData.map((item) => {
          const isSelected = selectedImages.includes(item.id);
          return (
            <div
              key={item.id}
              style={{
                position: "relative",
                border: isSelected ? "3px solid #f3691a" : "1px solid #ddd",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "pointer",
                opacity: selectMode && !isSelected ? 0.5 : 1,
                backgroundColor: "#fff",
              }}
              onClick={() => {
                if (selectMode) {
                  handleToggleSelect(item.id);
                } else {
                  if (item.tipo === "imagen") {
                    const allImages = filteredData
                      .filter((img) => img.tipo === "imagen")
                      .map((img) => img.url);
                    const currentIndex = allImages.findIndex(
                      (url) => url === item.url,
                    );
                    setPreviewImages(allImages);
                    setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
                    setOpenPreviewDialog(true);
                  } else {
                    // Para archivos, abrir en nueva pestaña
                    window.open(`${SERVER_URL}/${item.url}`, "_blank");
                  }
                }
              }}
            >
              {selectMode && (
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggleSelect(item.id)}
                  style={{
                    position: "absolute",
                    top: 5,
                    left: 5,
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  }}
                />
              )}
              {item.favorito && !selectMode && (
                <StarIcon
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    zIndex: 10,
                    color: "#f3691a",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: "50%",
                    padding: "4px",
                  }}
                />
              )}
              {item.tipo === "imagen" ? (
                <img
                  src={SERVER_URL + "/" + item.url}
                  alt={item.titulo || "Imagen"}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "200px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "200px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {getFileIcon(item.tipo, item.tipoArchivo)}
                  {item.tipoArchivo && (
                    <span
                      style={{
                        fontSize: "12px",
                        marginTop: "8px",
                        color: "#666",
                      }}
                    >
                      {item.tipoArchivo.toUpperCase()}
                    </span>
                  )}
                </div>
              )}
              <div style={{ padding: "10px" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    marginBottom: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{item.titulo || "Sin título"}</span>
                </div>
                {item.etiquetas && item.etiquetas.length > 0 && (
                  <div
                    style={{
                      marginTop: "5px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    {item.etiquetas.slice(0, 3).map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        style={{ fontSize: "10px", height: "20px" }}
                      />
                    ))}
                    {item.etiquetas.length > 3 && (
                      <Chip
                        label={`+${item.etiquetas.length - 3}`}
                        size="small"
                        style={{ fontSize: "10px", height: "20px" }}
                      />
                    )}
                  </div>
                )}
                {!selectMode && (
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      marginTop: "10px",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", gap: "5px" }}>
                      <Tooltip
                        title={
                          item.favorito
                            ? "Quitar de favoritos"
                            : "Añadir a favoritos"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(item.id, item.favorito);
                          }}
                          style={{ color: item.favorito ? "#f3691a" : "#ccc" }}
                        >
                          {item.favorito ? (
                            <StarIcon fontSize="small" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver dónde se usa">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowUsage(item.id);
                          }}
                          style={{ color: "#2196f3" }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push("/edit-imagen/" + item.id);
                          }}
                          style={{ color: "#f3691a" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                    {(user?.role === "admin" || user.role === "gestor") && (
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImagenId(item.id);
                            setOpenConfirmDialog(2);
                          }}
                          style={{ color: "#f64e60" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader title="Imagenes list">
          <CardHeaderToolbar>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {selectMode && selectedImages.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  style={{ backgroundColor: "#f64e60", color: "white" }}
                >
                  Eliminar seleccionadas ({selectedImages.length})
                </Button>
              )}
              <IconButton
                onClick={() => {
                  const newMode = viewMode === "table" ? "grid" : "table";
                  setViewMode(newMode);
                  localStorage.setItem("imagenesPage_viewMode", newMode);
                }}
                style={{ color: "#f3691a" }}
              >
                {viewMode === "table" ? <ViewModuleIcon /> : <ViewListIcon />}
              </IconButton>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => history.push("/edit-imagen")}
              >
                Añadir nueva
              </button>
            </div>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {/* Filtros */}
          <Card style={{ marginBottom: "15px", backgroundColor: "#f8f9fa" }}>
            <CardBody style={{ padding: "15px 20px" }}>
              {renderFiltersContent()}
            </CardBody>
          </Card>

          {/* Indicador de carga */}
          {loading && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Typography>Cargando imágenes...</Typography>
            </div>
          )}

          {/* Vista de tabla o cuadrícula */}
          {!loading && (
            <>
              {viewMode === "table" ? (
                <Table
                  search={false}
                  data={filteredData}
                  columns={columns}
                  pageKey="imagenesPage"
                  searchText={searchText}
                  onSearchChange={handleTableSearchChange}
                />
              ) : (
                renderGridView()
              )}

              {/* Paginación */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    style={{ borderColor: "#f3691a", color: "#f3691a" }}
                  >
                    Anterior
                  </Button>
                  <Typography>
                    Página {currentPage} de {totalPages} ({totalImages}{" "}
                    imágenes)
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    style={{ borderColor: "#f3691a", color: "#f3691a" }}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Diálogos de confirmación */}
          <ConfirmDialog
            title={"¿Estás seguro de que quieres eliminar esta imagen?"}
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteImagen(imagenId)
                .then((res) => {
                  if (res.status === 204 || res.status === 200) {
                    alertSuccess({
                      title: "¡Eliminada!",
                      customMessage: "Imagen eliminada correctamente.",
                    });
                    setRefresh(true);
                    setSelectedImages((prev) =>
                      prev.filter((id) => id !== imagenId),
                    );
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "No se ha podido eliminar la imagen.",
                  });
                });
            }}
          />
          <ConfirmDialog
            title={`¿Estás seguro de que quieres eliminar ${selectedImages.length} imagen(es)?`}
            open={openConfirmDialog === 3}
            setOpen={setOpenConfirmDialog}
            onConfirm={async () => {
              try {
                const deletePromises = selectedImages.map((id) =>
                  deleteImagen(id),
                );
                await Promise.all(deletePromises);
                alertSuccess({
                  title: "¡Eliminadas!",
                  customMessage: `${selectedImages.length} imagen(es) eliminada(s) correctamente.`,
                });
                setSelectedImages([]);
                setSelectMode(false);
                setRefresh(true);
              } catch (error) {
                alertError({
                  error: error,
                  customMessage: "No se han podido eliminar algunas imágenes.",
                });
                setRefresh(true);
              }
            }}
          />
          <ImageCarouselDialog
            open={openPreviewDialog}
            setOpen={setOpenPreviewDialog}
            images={previewImages}
            initialIndex={currentImageIndex}
            imageTitles={filteredData.map((item) => item.titulo)}
          />

          {/* Diálogo de información de uso */}
          <Dialog
            open={usageDialogOpen}
            onClose={() => setUsageDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Dónde se usa esta imagen</DialogTitle>
            <DialogContent>
              {loadingUsage ? (
                <Typography>Cargando información...</Typography>
              ) : usageData ? (
                <div>
                  {usageData.cursos.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <Typography
                        variant="h6"
                        style={{ marginBottom: "10px", color: "#f3691a" }}
                      >
                        Cursos ({usageData.cursos.length})
                      </Typography>
                      {usageData.cursos.map((curso) => (
                        <Chip
                          key={curso._id}
                          label={curso.titulo}
                          onClick={() =>
                            history.push(`/edit-curso/${curso._id}`)
                          }
                          style={{ margin: "5px", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                  )}
                  {usageData.noticias.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <Typography
                        variant="h6"
                        style={{ marginBottom: "10px", color: "#f3691a" }}
                      >
                        Noticias ({usageData.noticias.length})
                      </Typography>
                      {usageData.noticias.map((noticia) => (
                        <Chip
                          key={noticia._id}
                          label={noticia.titulo}
                          onClick={() =>
                            history.push(`/edit-noticia/${noticia._id}`)
                          }
                          style={{ margin: "5px", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                  )}
                  {usageData.eventos.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <Typography
                        variant="h6"
                        style={{ marginBottom: "10px", color: "#f3691a" }}
                      >
                        Eventos ({usageData.eventos.length})
                      </Typography>
                      {usageData.eventos.map((evento) => (
                        <Chip
                          key={evento._id}
                          label={evento.titulo}
                          onClick={() =>
                            history.push(`/edit-evento/${evento._id}`)
                          }
                          style={{ margin: "5px", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                  )}
                  {usageData.banners.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <Typography
                        variant="h6"
                        style={{ marginBottom: "10px", color: "#f3691a" }}
                      >
                        Banners ({usageData.banners.length})
                      </Typography>
                      {usageData.banners.map((banner) => (
                        <Chip
                          key={banner._id}
                          label={banner.titulo}
                          onClick={() =>
                            history.push(`/edit-banner/${banner._id}`)
                          }
                          style={{ margin: "5px", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                  )}
                  {usageData.cursos.length === 0 &&
                    usageData.noticias.length === 0 &&
                    usageData.eventos.length === 0 &&
                    usageData.banners.length === 0 && (
                      <Typography>
                        Esta imagen no se está usando en ningún lugar.
                      </Typography>
                    )}
                </div>
              ) : (
                <Typography>No se pudo cargar la información.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setUsageDialogOpen(false)}
                variant="outlined"
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        </CardBody>
      </Card>
    </>
  );
}
